import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"

import type {
    TCoachingSessionsCancelModalProps as TCancelSessionModalProps,
    TCoachingSessionsModalSimpleProps as TGiftSessionModalProps,
    TCoachingSessionsTimingSessionModalProps as TTimingSessionModalProps
} from "@/3514/entities/coaching-sessions"
import {
    EAttendanceModalOrigin,
    type TAttendanceModalProps,
    attendanceMarksQueryPrefetcher,
    isEnoughTimeToMarkAttendance
} from "@/3514/modals/attendance"
import {
    ESessionScheduleModalMode,
    ESessionScheduleModalOrigin,
    type TSessionScheduleModalProps,
    coachTimeWidgetQueryPrefetcher
} from "@/3514/modals/session-schedule"
import { EModalId } from "@/3514/store/modal"
import { EUserRole } from "@/3514/types"
import { emptyAsyncCallback } from "@/shared/types/functions"

import {
    type _TSessionInfoRenderFunctionSharedExternalParams as TFunctionExternalParams,
    type _TSessionInfoRenderFunctionSharedInternalParams as TFunctionInternalParams,
    type TSelectInfoDropdownItem
} from "./session-info.types"
import utils from "./session-info.utils"

function renderSwapSessionActionProps({
    data: { session },
    t,
    navigate
}: TFunctionInternalParams & TFunctionExternalParams): TSelectInfoDropdownItem {
    return {
        label: t("entities.sessions.actions.swap.label"),
        ariaLabel: t("entities.sessions.actions.swap.ariaLabel"),
        actionHandler: async (): Promise<void> => await navigate(`/swap-session/${session.id}`),
        focusHandler: emptyAsyncCallback
    }
}

function renderScheduleSessionActionProps({
    data: { session, coach, extraParties, user },
    sessionsQueryKey,
    isNetworkFast,
    viewerRole,
    t,
    qc,
    modalDispatcher
}: TFunctionInternalParams & TFunctionExternalParams): TSelectInfoDropdownItem {
    const _isEmptyStartDate: boolean = isEmpty(session.startDate)
    const _isEnoughTime: boolean = utils.isEnoughTimeToEdit(session.startDate, session.minHoursToEdit)
    const _withNotice: boolean = includes<EUserRole>([EUserRole.Participant, EUserRole.Assistant], viewerRole)

    return {
        label: _isEmptyStartDate
            ? t("entities.sessions.actions.schedule.label")
            : t("entities.sessions.actions.reschedule.label"),
        ariaLabel: _isEmptyStartDate
            ? t("entities.sessions.actions.schedule.ariaLabel")
            : t("entities.sessions.actions.reschedule.ariaLabel"),
        actionHandler: (): void => {
            if (Boolean(_withNotice && !_isEnoughTime))
                modalDispatcher.showModal<TTimingSessionModalProps>(EModalId.LateEditSession, {
                    advanceTimeInHours: session.minHoursToEdit
                })

            if (Boolean(Boolean(_withNotice && _isEnoughTime) || !_withNotice))
                modalDispatcher.showModal<TSessionScheduleModalProps>(EModalId.ScheduleSession, {
                    isExtraPartySession: session.isExtraParty && !isEmpty(extraParties),
                    sessionId: session.id,
                    queryKey: sessionsQueryKey,
                    user,
                    participantId: user.id,
                    origin: ESessionScheduleModalOrigin.Sessions,
                    mode: _isEmptyStartDate ? ESessionScheduleModalMode.Schedule : ESessionScheduleModalMode.Reschedule,
                    withCloseOnEscapeClick: true
                })
        },
        focusHandler: (): void => {
            if (isNetworkFast && _isEnoughTime)
                void coachTimeWidgetQueryPrefetcher(
                    { qc },
                    { coachId: coach.id, sessionId: session.id, participantId: user.id }
                )
        }
    }
}

function renderCancelSessionActionProps({
    data: { session },
    viewerRole,
    sessionsQueryKey,
    t,
    modalDispatcher
}: TFunctionInternalParams & TFunctionExternalParams): TSelectInfoDropdownItem {
    const _withNotice: boolean = includes<EUserRole>([EUserRole.Participant, EUserRole.Assistant], viewerRole)
    const _isEnoughTime: boolean = utils.isEnoughTimeToEdit(session.startDate, session.minHoursToEdit)

    return {
        label: t("entities.sessions.actions.cancel.label"),
        ariaLabel: t("entities.sessions.actions.cancel.ariaLabel"),
        actionHandler: (): void => {
            if (_withNotice && !_isEnoughTime)
                modalDispatcher.showModal<TTimingSessionModalProps>(EModalId.LateEditSession, {
                    advanceTimeInHours: session.minHoursToEdit
                })

            if (Boolean(Boolean(_withNotice && _isEnoughTime)) || !_withNotice)
                modalDispatcher.showModal<TCancelSessionModalProps>(EModalId.CancelSession, {
                    sessionsQueryKey,
                    sessionId: session.id,
                    viewerRole
                })
        },
        focusHandler: emptyAsyncCallback
    }
}

function renderAttendanceActionProps({
    data: { session, user },
    t,
    isNetworkFast,
    qc,
    modalDispatcher
}: TFunctionInternalParams & TFunctionExternalParams): TSelectInfoDropdownItem {
    return {
        label: t("entities.sessions.actions.attendance.label"),
        ariaLabel: t("entities.sessions.actions.attendance.ariaLabel"),
        actionHandler: (): void =>
            modalDispatcher.showModal<TAttendanceModalProps>(EModalId.Attendance, {
                origin: EAttendanceModalOrigin.CoachingSessions,
                sessionId: session.id,
                sessionModality: session.modality,
                sessionStartTime: session.startDate,
                minHoursToMark: session.minHoursToEdit,
                groupId: session.groupId,
                participantId: user.id
            }),
        focusHandler: async (): Promise<void> => {
            if (isNetworkFast && isEnoughTimeToMarkAttendance(session.startDate, session.minHoursToEdit))
                await attendanceMarksQueryPrefetcher({ qc }, { sessionId: session.id })
        }
    }
}

function renderGiftSessionActionProps({
    data: { session },
    sessionsQueryKey,
    t,
    modalDispatcher
}: TFunctionInternalParams & TFunctionExternalParams): TSelectInfoDropdownItem {
    return {
        label: t("entities.sessions.actions.gift.label"),
        ariaLabel: t("entities.sessions.actions.gift.ariaLabel"),
        actionHandler: (): void =>
            session.isGifted
                ? modalDispatcher.showModal<TGiftSessionModalProps>(EModalId.Coach_GiftedSession, {
                      sessionsQueryKey,
                      sessionId: session.id
                  })
                : modalDispatcher.showModal<TGiftSessionModalProps>(EModalId.Coach_GiftSession, {
                      sessionsQueryKey,
                      sessionId: session.id
                  }),
        focusHandler: emptyAsyncCallback
    }
}

export default {
    renderSwapSessionActionProps,
    renderScheduleSessionActionProps,
    renderCancelSessionActionProps,
    renderAttendanceActionProps,
    renderGiftSessionActionProps
}
